import { t } from 'i18next'

import { currentEnv } from '@/env/vars'
import { store } from '@/store'
import { idTokenAtom } from '@/store/app/AppAtom'
import { networkErrorAtom } from '@/store/app/NetworkErrorAtom.ts'
import { addNotification } from '@/store/gigiChat/Notifications'
import { TCacheControl } from '@/types/app/App'

import { acquireTokenSilent, pca } from './MSAL'

export const getToken = async (type: 'id' | 'access' | 'both' = 'id') => {
    const res = await acquireTokenSilent(pca)
    store.set(idTokenAtom, res.idToken)
    if (type === 'access') return res.accessToken
    if (type === 'both') return { idToken: res.idToken, accessToken: res.accessToken }
    return res.idToken
}

const isChat = (url: string) => {
    return url.includes(currentEnv.chatUrl)
}

const isVisuals = (url: string) => {
    return url.includes('/gigicore')
}

export const handleErrorMessages = (response: Response) => {
    const status = response.status.toString()
    if (status.startsWith('50')) {
        addNotification('error', t('global.error.500'))
    } else {
        switch (status) {
            case '400':
                addNotification('error', t('global.error.400'))
                break
            case '401':
                addNotification('error', t('global.error.401'))
                break
            case '403':
                addNotification('error', t('global.error.403'))
                break
            case '404':
                addNotification('error', t('global.error.404'))
                break
            default:
                addNotification('error', t('global.error.wrong'))
        }
    }
}

const headersForGet = (URL: string, idToken: string, accessToken: string) => {
    const headers: {
        Authorization: string
        //gsk-apiqw-client-id it's used as a feature flag on platform side at some point some systems
        //from approval or compliances may be received or not on web
        'gsk-apiqw-client-id'?: string
        'gsk-apiqw-access-token'?: string
        'dev-gsk-apigw-user-mud-id'?: string
        'x-app-identifier'?: string
        credentials?: string
        'cache-control'?: TCacheControl
    } = {
        Authorization: `Bearer ${idToken}`,
    }

    if (URL.includes('compliances') || URL.includes('approvals')) {
        headers['gsk-apiqw-client-id'] = 'GIGI_WEB'
        // this allows testers to fake being another user, only works in DEV
        const spoofUser: string | null = localStorage.getItem('spoofUser')
        if (spoofUser) {
            headers['dev-gsk-apigw-user-mud-id'] = spoofUser
        }
    }

    if (isChat(URL) || isVisuals(URL)) {
        headers['x-app-identifier'] = 'gigi'
        headers['gsk-apiqw-access-token'] = accessToken
    }

    return headers
}

const handleResponse = (response: Response, hideError?: boolean) => {
    if (!response.ok && !hideError) {
        // not approvals or compliances
        if (!response.url.includes('approvals') && !response.url.includes('compliances')) {
            if (response.status === 413) {
                return response
            } else {
                addNotification('error', t('global.error.wrong'))
            }
        } else {
            handleErrorMessages(response)
        }
        return
    }
    return response
}

const handleCatch = (error: unknown, hideError?: boolean) => {
    if (!hideError) {
        addNotification('error', t('global.error.wrong'))
    }
    console.error('ERROR: ', error)
}

export interface IGetOptions {
    hideError: boolean
    cacheControl?: TCacheControl
    signal?: AbortSignal
}

export const GET = async <T = unknown>(URL: string, options?: IGetOptions): Promise<T | void> => {
    const idToken: string = (await getToken()) as string
    const accessToken = (await getToken('access')) as string
    const headers = headersForGet(URL, idToken, accessToken)
    const isAsyncProcess = URL.includes('/asyncprocess')

    const requestOptions: RequestInit = {
        headers,
    }

    if (isChat(URL)) {
        requestOptions.credentials = 'include'
    }

    if (isAsyncProcess) {
        headers['x-app-identifier'] = 'gigi'
    }

    if (options?.cacheControl) {
        headers['cache-control'] = options.cacheControl
    }

    if (options?.signal) {
        requestOptions.signal = options.signal
    }

    return await fetch(URL, requestOptions)
        .then((response) => {
            if (!response.ok) {
                // not approvals or compliances
                if (!response.url.includes('approvals') && !response.url.includes('compliances')) {
                    throw Error(response.statusText)
                } else {
                    handleErrorMessages(response)
                    return response as T
                }
            }
            return response.json() as T
        })
        .catch((error) => {
            const safariErrorMessage = 'Load failed'
            const edgeErrorMessage = 'Failed to fetch'
            if (error && error instanceof Error && [safariErrorMessage, edgeErrorMessage].includes(error.message)) {
                // this should only catch Fetch thrown errors like CORS or networking issue
                store.set(networkErrorAtom, 'NETWORK')
            } else {
                // errors thrown by !ok response
                if (!options?.hideError) {
                    addNotification('error', t('global.error.wrong'))
                }
            }
            console.error('ERROR: ', error)
        })
}

export const SIMPLE_GET = async (URL: string, hideError?: boolean) => {
    const idToken: string = (await getToken()) as string
    const accessToken = (await getToken('access')) as string

    const requestOptions: RequestInit = {
        headers: headersForGet(URL, idToken, accessToken),
        credentials: isChat(URL) ? 'include' : 'omit',
    }

    return await fetch(URL, requestOptions)
        .then((res) => handleResponse(res, hideError ?? false))
        .catch((err) => handleCatch(err, hideError ?? false))
}

export const POST = async (URL: string, body: unknown, isFile?: boolean, hideError?: boolean) => {
    const idToken = (await getToken()) as string
    const accessToken = (await getToken('access')) as string

    let _body: string | FormData = JSON.stringify(body)
    let _headers: HeadersInit = {
        'Content-type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${idToken}`,
    }

    if (isFile) {
        _body = body as FormData
        _headers = { Authorization: `Bearer ${idToken}` }
    }

    if (isChat(URL) || isVisuals(URL)) {
        _headers['x-app-identifier'] = 'gigi'
        _headers['gsk-apiqw-access-token'] = accessToken
    }

    const requestOptions: RequestInit = {
        method: 'POST',
        body: _body,
        headers: _headers,
        credentials: isChat(URL) ? 'include' : 'omit',
    }

    return await fetch(URL, requestOptions)
        .then((res) => handleResponse(res, hideError ?? false))
        .catch((err) => handleCatch(err, hideError ?? false))
}

export const PUT = async (URL: string, body: unknown, headers: object = {}, hideError?: boolean) => {
    const idToken: string = (await getToken()) as string
    const accessToken = (await getToken('access')) as string
    const additionalHeaders: HeadersInit = {}

    if (isChat(URL)) {
        additionalHeaders['x-app-identifier'] = 'gigi'
        additionalHeaders['gsk-apiqw-access-token'] = accessToken
    }

    const requestOptions: RequestInit = {
        method: 'PUT',
        body: JSON.stringify(body),
        credentials: isChat(URL) ? 'include' : 'omit',
        headers: {
            'Content-type': 'application/json; charset=UTF-8',
            Authorization: `Bearer ${idToken}`,
            ...headers,
            ...additionalHeaders,
        },
    }

    return await fetch(URL, requestOptions)
        .then((res) => handleResponse(res, hideError ?? false))
        .catch((err) => handleCatch(err, hideError ?? false))
}

export const DELETE = async (URL: string, hideError?: boolean) => {
    const idToken: string = (await getToken()) as string
    const accessToken = (await getToken('access')) as string
    const additionalHeaders: HeadersInit = {}

    if (isChat(URL)) {
        additionalHeaders['x-app-identifier'] = 'gigi'
        additionalHeaders['gsk-apiqw-access-token'] = accessToken
    }

    const requestOptions: RequestInit = {
        method: 'DELETE',
        credentials: isChat(URL) ? 'include' : 'omit',
        headers: {
            Authorization: `Bearer ${idToken}`,
            ...additionalHeaders,
        },
    }

    return await fetch(URL, requestOptions)
        .then((res) => handleResponse(res, hideError ?? false))
        .catch((err) => handleCatch(err, hideError ?? false))
}
